<script lang="ts">
  import type { OrganizationFrame } from "@kontextwerk/control-web-client"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { onMount } from "svelte"
  import {
    fetchHomeDashboard,
    type HomeDashboardResponse,
  } from "../../../../../../../../api/kehrblatt/home-dashboard"
  import type { OrganizationOnboardingResponse } from "../../../../../../../../api/kehrblatt/organization-onboarding"
  import { currentWeekRange } from "../../../../_shared/_logic/home-dashboard"
  import HomeDashboard from "./_modules/home-dashboard"
  import OrganizationOnboardingPanel from "./_modules/organization-onboarding-panel"
  import TutorialPanel from "./_modules/tutorial-panel"
  import { getLearningProgress } from "../../../../../../_shared/_logic/learning-progress"

  const {
    organization,
    currentUserId,
    inboxPath = null,
    onOpenInbox = null,
    canManageSetup = false,
    canUseOperationalPlanning = false,
    showOpenTasks = true,
  }: {
    organization: OrganizationFrame
    currentUserId: string
    inboxPath?: string | null
    onOpenInbox?: (() => void) | null
    canManageSetup?: boolean
    canUseOperationalPlanning?: boolean
    showOpenTasks?: boolean
  } = $props()

  const learningProgress = getLearningProgress()
  const courseCompleted = $derived(
    learningProgress.course !== null &&
      learningProgress.course.total_count > 0 &&
      learningProgress.course.completed_count >= learningProgress.course.total_count,
  )

  let overview = $state<HomeDashboardResponse | null>(null)
  let initialLoading = $state(true)
  let refreshing = $state(false)
  let error = $state<string | null>(null)
  let activeRequest: Promise<void> | null = null
  let activeController: AbortController | null = null
  let requestGeneration = 0
  let destroyed = false
  const range = currentWeekRange()

  const loadOverview = (): Promise<void> => {
    if (activeRequest) return activeRequest

    const controller = new AbortController()
    const generation = ++requestGeneration
    const retainingContent = overview !== null
    activeController = controller
    error = null

    if (retainingContent) {
      refreshing = true
    } else {
      initialLoading = true
    }

    const requestPromise = (async () => {
      try {
        const response = await fetchHomeDashboard(organization.id, range, {
          signal: controller.signal,
        })

        if (destroyed || controller.signal.aborted || generation !== requestGeneration) return

        overview = response
      } catch (err) {
        if (destroyed || controller.signal.aborted || generation !== requestGeneration) return

        console.error("home_overview_load_failed", err)
        error = "Die Startseite konnte nicht vollständig geladen werden."
      } finally {
        if (destroyed || generation !== requestGeneration) return

        initialLoading = false
        refreshing = false

        if (activeController === controller) activeController = null

        if (activeRequest === requestPromise) activeRequest = null
      }
    })()
    activeRequest = requestPromise

    return requestPromise
  }

  const retry = () => {
    void loadOverview()
  }

  const handleOnboardingUpdated = (onboarding: OrganizationOnboardingResponse) => {
    if (!overview) return

    overview = {
      ...overview,
      onboarding: { state: "READY", data: onboarding },
    }
  }

  onMount(() => {
    void loadOverview()

    return () => {
      destroyed = true
      requestGeneration += 1
      activeController?.abort()
      activeController = null
      activeRequest = null
    }
  })
</script>

<div class="home-overview">
  {#if !courseCompleted}
    <TutorialPanel organizationSlug={organization.slug} />
  {/if}
  <div class="home-overview__dashboard" aria-busy={initialLoading || refreshing}>
  {#if overview}
    {#if refreshing}
      <div class="refresh-state" role="status">Startseite wird aktualisiert …</div>
    {:else if error}
      <div class="refresh-state refresh-state--error" role="alert">
        <span>{error}</span>
        <CTA
            type="secondaryButton"
            small={true}
            content="Erneut laden"
            darkMode={false}
            onClick={retry}
          />
      </div>
    {/if}

    {#if overview.onboarding.state !== "HIDDEN"}
      <OrganizationOnboardingPanel
          organizationId={organization.id}
          organizationSlug={organization.slug}
          organizationName={organization.name}
          {currentUserId}
          onboarding={overview.onboarding.data}
          state={overview.onboarding.state}
          {refreshing}
          {canUseOperationalPlanning}
          onRetry={retry}
          onUpdated={handleOnboardingUpdated}
          onRefreshRequested={retry}
        />
    {/if}

    <HomeDashboard
        tasks={overview.tasks}
        {inboxPath}
        {onOpenInbox}
        onRetry={retry}
        {showOpenTasks}
      />
  {:else}
    {#if error}
      <div class="initial-error" role="alert">
        <span>{error}</span>
        <CTA
            type="secondaryButton"
            small={true}
            content="Erneut laden"
            darkMode={false}
            onClick={retry}
          />
      </div>
    {/if}
    {#if initialLoading}
    <div class="overview-skeleton" data-testid="home-overview.skeleton">
      {#if canManageSetup}
        <div class="skeleton-section skeleton-section--onboarding"></div>
      {/if}
      {#if showOpenTasks}
        <div class="skeleton-section skeleton-section--tasks"></div>
      {/if}
    </div>
    {/if}
  {/if}
  </div>
  {#if courseCompleted}
    <TutorialPanel organizationSlug={organization.slug} />
  {/if}
</div>

<style lang="less">
  .home-overview {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-width: 0;
  }

  .home-overview__dashboard {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .overview-skeleton {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .skeleton-section {
    border: 1px solid color-mix(in srgb, var(--text-invers-100) 10%, transparent);
    background: linear-gradient(
      90deg,
      color-mix(in srgb, var(--text-invers-100) 5%, transparent),
      color-mix(in srgb, var(--text-invers-100) 10%, transparent),
      color-mix(in srgb, var(--text-invers-100) 5%, transparent)
    );
    background-size: 220% 100%;
    min-height: 7.5rem;
    animation: skeleton-shimmer 1.7s linear infinite;
  }

  .skeleton-section--onboarding {
    min-height: 9rem;
  }

  .initial-error,
  .refresh-state {
    border: 1px solid color-mix(in srgb, var(--warning-100, #e07a00) 44%, transparent);
    background: var(--neutral-white);
    color: var(--text-invers-100);
    padding: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .initial-error {
    margin-bottom: 1rem;
  }

  .refresh-state--error {
    color: var(--error-color, #d34b4b);
  }

  @keyframes skeleton-shimmer {
    from {
      background-position: 100% 0;
    }
    to {
      background-position: -100% 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton-section {
      animation: none;
    }
  }
</style>
